import * as Sentry from '@sentry/react';
import { omit } from 'lodash';
import Q from 'q';
// This exports WebVTT on the window object.
import 'video.js';

import API from 'js/lib/api';

import Cue from 'bundles/interactive-transcript/models/Cue';
import Track from 'bundles/interactive-transcript/models/Track';
import TrackList from 'bundles/interactive-transcript/models/TrackList';
import StorageUtils from 'bundles/interactive-transcript/utils/StorageUtils';
import { parse as nodeWebVttParse } from 'bundles/interactive-transcript/utils/webvttParser';
import inServerContext from 'bundles/ssr/util/inServerContext';

declare const WebVTT: any;

declare global {
  interface Window {
    vttjs: any;
  }
}

export const parseCuesFromServerContext = (
  sourceContent: any,
  trimmedStartTimeSeconds?: number,
  trimmedEndTimeSeconds?: number | null
): Cue[] => {
  const timeFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  });

  const cues: any[] = nodeWebVttParse(sourceContent).cues;
  return cues
    .filter((cue) => !!cue)
    .map((cue, index) => {
      // On server side, we're using node-webvtt which returns slightly different property name
      return new Cue(
        cue.identifier,
        index,
        cue.text,
        Number(timeFormatter.format(cue.start)),
        Number(timeFormatter.format(cue.end)),
        trimmedStartTimeSeconds,
        trimmedEndTimeSeconds
      );
    })
    .filter((cue) => !!cue.text.trim());
};

export const parseCues = (
  sourceContent: any,
  trimmedStartTimeSeconds?: number,
  trimmedEndTimeSeconds?: number | null
): Cue[] => {
  if (inServerContext) {
    return parseCuesFromServerContext(sourceContent, trimmedStartTimeSeconds, trimmedEndTimeSeconds);
  }

  const cues: any[] = [];
  const parser = new WebVTT.Parser(window, window.vttjs, new WebVTT.StringDecoder());

  parser.oncue = (cue: any) => cues.push(cue);
  parser.onparsingerror = (error: $TSFixMe) => {
    throw new Error('Error parsing track: ' + error);
  };

  parser.parse(sourceContent);
  parser.flush();

  return cues
    .filter((cue) => !!cue)
    .map((cue: any, index) => {
      return new Cue(
        cue.id,
        index,
        cue.text,
        cue.startTime,
        cue.endTime,
        trimmedStartTimeSeconds,
        trimmedEndTimeSeconds
      );
    })
    .filter((cue) => !!cue.text.trim());
};

export const loadTrack = (
  track: Track,
  trimmedStartTimeSeconds?: number,
  trimmedEndTimeSeconds?: number | null,
  forceReload?: boolean
): Q.Promise<Track> => {
  if (track.isLoaded && !forceReload) {
    return Q(track);
  }

  const constructCues = (data: $TSFixMe) => {
    const _track = track;
    _track.sourceContent = data;
    _track.isLoaded = true;

    let cues: Cue[] = [];
    try {
      cues = parseCues(data, trimmedStartTimeSeconds, trimmedEndTimeSeconds);
    } catch (e) {
      Sentry.captureException('cannot parse cues', { extra: { track, error: e } });
    }
    _track.cues = cues;

    return _track;
  };

  const api = API('', { type: 'rest' });
  return (
    Q(api.get(track.url))
      .then(constructCues)
      // @ts-expect-error TSMIGRATION
      .catch((xhr: any, status: string, error: string) => {
        throw new Error(
          `Error loading track. Error: ${error}; status: ${status}; response text: ${JSON.stringify(xhr)}`
        );
      })
  );
};

export const buildTracks = (languages: { [lang: string]: { label: string; url: string } }): TrackList => {
  const tracks: Array<Track> = [];
  // Will treat the __typename field as a separate language if this is not included
  const _filteredLanguages = omit(languages, '__typename');

  Object.keys(_filteredLanguages).forEach((key) => {
    const language = languages[key];
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    tracks.push(new Track(key, language.label, language.url));
  });

  return new TrackList(tracks);
};

export const getDefaultTrack = (tracks: TrackList, defaultLanguage: string) => {
  return (
    // If the user has selected a language previously, use that.
    (!inServerContext && tracks.getFromLanguageCode(StorageUtils.get('language-code'))) ||
    // If not, or if that language is not in the tracks, use the configured default language.
    tracks.getFromLanguageCode(defaultLanguage) ||
    // If the configured default language is not in the tracks, use english.
    tracks.getFromLanguageCode('en') ||
    // If even english subtitles are not found, use the first track in the list. If there is no track,
    // then there are no subtitles and the transcript will not be visible.
    tracks.toArray()[0]
  );
};
